import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  serverTimestamp,
  orderBy,
  limit,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import BookService from '../services/BookService';
import './BookRecommendations.css';

function BookRecommendations() {
  const [activeTab, setActiveTab] = useState('received');
  const [recommendations, setRecommendations] = useState([]);
  const [friends, setFriends] = useState([]);
  const [userBooks, setUserBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [recommendationNote, setRecommendationNote] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    fetchData();
  }, [currentUser, activeTab]);

  const fetchData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      await Promise.all([
        fetchRecommendations(),
        fetchFriends(),
        fetchUserBooks()
      ]);
    } catch (error) {
      console.error('Error fetching recommendation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      let recommendationsQuery;
      
      if (activeTab === 'received') {
        // Get recommendations TO current user
        recommendationsQuery = query(
          collection(db, 'recommendations'),
          where('toUserId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
      } else {
        // Get recommendations FROM current user
        recommendationsQuery = query(
          collection(db, 'recommendations'),
          where('fromUserId', '==', currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
      }

      const querySnapshot = await getDocs(recommendationsQuery);
      const recommendationsData = [];
      
      for (const docSnap of querySnapshot.docs) {
        const recommendation = { id: docSnap.id, ...docSnap.data() };
        
        // Get user profiles for both users
        const fromUserDoc = await getDoc(doc(db, 'userProfiles', recommendation.fromUserId));
        const toUserDoc = await getDoc(doc(db, 'userProfiles', recommendation.toUserId));
        
        if (fromUserDoc.exists()) {
          recommendation.fromUserProfile = fromUserDoc.data();
        }
        if (toUserDoc.exists()) {
          recommendation.toUserProfile = toUserDoc.data();
        }
        
        recommendationsData.push(recommendation);
      }
      
      setRecommendations(recommendationsData);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  };

  const fetchFriends = async () => {
    try {
      const friendsQuery = query(
        collection(db, 'friendships'),
        where('users', 'array-contains', currentUser.uid),
        where('status', '==', 'accepted')
      );

      const querySnapshot = await getDocs(friendsQuery);
      const friendsData = [];

      for (const docSnap of querySnapshot.docs) {
        const friendship = docSnap.data();
        const friendId = friendship.users.find(id => id !== currentUser.uid);
        
        // Get friend's profile
        const friendDoc = await getDoc(doc(db, 'userProfiles', friendId));
        if (friendDoc.exists()) {
          friendsData.push({
            id: friendId,
            ...friendDoc.data()
          });
        }
      }

      setFriends(friendsData);
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };

  const fetchUserBooks = async () => {
    try {
      const books = await BookService.getUserBooks(currentUser.uid);
      setUserBooks(books);
    } catch (error) {
      console.error('Error fetching user books:', error);
    }
  };

  const sendRecommendation = async () => {
    if (!selectedFriend || !selectedBook) return;

    try {
      await addDoc(collection(db, 'recommendations'), {
        fromUserId: currentUser.uid,
        toUserId: selectedFriend.id,
        bookTitle: selectedBook.title,
        bookAuthors: selectedBook.authors || ['Unknown Author'],
        bookImageUrl: selectedBook.imageUrl || null,
        bookDescription: selectedBook.description || '',
        bookGoogleBooksId: selectedBook.googleBooksId || null,
        note: recommendationNote.trim() || '',
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Reset form
      setSelectedFriend(null);
      setSelectedBook(null);
      setRecommendationNote('');
      setShowRecommendModal(false);
      
      // Refresh recommendations if on sent tab
      if (activeTab === 'sent') {
        fetchRecommendations();
      }
      
      alert('Book recommendation sent successfully!');
    } catch (error) {
      console.error('Error sending recommendation:', error);
      alert('Failed to send recommendation. Please try again.');
    }
  };

  const addRecommendedBook = async (recommendation) => {
    try {
      const bookData = {
        userId: currentUser.uid,
        title: recommendation.bookTitle,
        authors: recommendation.bookAuthors,
        description: recommendation.bookDescription || '',
        imageUrl: recommendation.bookImageUrl || null,
        readingStatus: 'toRead',
        pageCount: null,
        categories: [],
        publishedDate: '',
        publisher: '',
        language: 'en',
        isbn: null,
        googleBooksId: recommendation.bookGoogleBooksId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        rating: null,
        mood: null,
        recommendedBy: recommendation.fromUserId
      };

      await BookService.addBook(currentUser.uid, bookData);
      alert('Book added to your library!');
    } catch (error) {
      console.error('Error adding recommended book:', error);
      if (error.message.includes('already exists')) {
        alert('This book is already in your library!');
      } else {
        alert('Failed to add book. Please try again.');
      }
    }
  };

  const RecommendationCard = ({ recommendation }) => {
    const isReceived = activeTab === 'received';
    const otherUser = isReceived ? recommendation.fromUserProfile : recommendation.toUserProfile;
    const userName = otherUser?.displayName || 'Unknown User';

    return (
      <div className="recommendation-card">
        <div className="recommendation-header">
          <div className="user-avatar">
            {userName.charAt(0).toUpperCase()}
          </div>
          <div className="recommendation-info">
            <div className="recommendation-message">
              {isReceived 
                ? `${userName} recommended "${recommendation.bookTitle}"`
                : `You recommended "${recommendation.bookTitle}" to ${userName}`
              }
            </div>
            <div className="recommendation-time">
              {recommendation.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
            </div>
          </div>
        </div>

        <div className="book-recommendation-content">
          {recommendation.bookImageUrl && (
            <img 
              src={recommendation.bookImageUrl} 
              alt={recommendation.bookTitle}
              className="book-thumbnail"
            />
          )}
          <div className="book-details">
            <h4>{recommendation.bookTitle}</h4>
            <p className="book-authors">
              {recommendation.bookAuthors?.join(', ') || 'Unknown Author'}
            </p>
            {recommendation.note && (
              <div className="recommendation-note">
                <strong>"{recommendation.note}"</strong>
              </div>
            )}
          </div>
        </div>

        {isReceived && (
          <div className="recommendation-actions">
            <button 
              className="btn btn-primary"
              onClick={() => addRecommendedBook(recommendation)}
            >
              📚 Add to Library
            </button>
          </div>
        )}
      </div>
    );
  };

  const RecommendModal = () => (
    <div className="modal-overlay">
      <div className="recommend-modal-content">
        <div className="modal-header">
          <h2>📨 Recommend a Book</h2>
          <button className="close-button" onClick={() => setShowRecommendModal(false)}>×</button>
        </div>

        <div className="recommend-form">
          <div className="form-group">
            <label>Select a Friend</label>
            <div className="friends-grid">
              {friends.map(friend => (
                <div 
                  key={friend.id}
                  className={`friend-option ${selectedFriend?.id === friend.id ? 'selected' : ''}`}
                  onClick={() => setSelectedFriend(friend)}
                >
                  <div className="friend-avatar">
                    {friend.displayName?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span>{friend.displayName || 'Unknown User'}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Select a Book from Your Library</label>
            <div className="books-grid">
              {userBooks.slice(0, 20).map(book => (
                <div 
                  key={book.id}
                  className={`book-option ${selectedBook?.id === book.id ? 'selected' : ''}`}
                  onClick={() => setSelectedBook(book)}
                >
                  {book.imageUrl && (
                    <img src={book.imageUrl} alt={book.title} className="book-thumb" />
                  )}
                  <div className="book-info">
                    <div className="book-title">{book.title}</div>
                    <div className="book-author">{book.authors?.join(', ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Add a Personal Note (Optional)</label>
            <textarea
              value={recommendationNote}
              onChange={(e) => setRecommendationNote(e.target.value)}
              placeholder="Why do you recommend this book?"
              className="recommendation-textarea"
              rows={3}
            />
          </div>

          <div className="modal-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => setShowRecommendModal(false)}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary"
              onClick={sendRecommendation}
              disabled={!selectedFriend || !selectedBook}
            >
              📨 Send Recommendation
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="recommendations-page">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading recommendations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recommendations-page">
      <div className="container">
        <div className="recommendations-header">
          <h1>📚 Book Recommendations</h1>
          <p>Share great books with your friends</p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowRecommendModal(true)}
          >
            📨 Recommend a Book
          </button>
        </div>

        <div className="recommendations-tabs">
          <button 
            className={`tab-btn ${activeTab === 'received' ? 'active' : ''}`}
            onClick={() => setActiveTab('received')}
          >
            📬 Received ({recommendations.length})
          </button>
          <button 
            className={`tab-btn ${activeTab === 'sent' ? 'active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            📤 Sent
          </button>
        </div>

        <div className="recommendations-content">
          {recommendations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <h3>
                {activeTab === 'received' 
                  ? 'No recommendations received yet' 
                  : 'No recommendations sent yet'
                }
              </h3>
              <p>
                {activeTab === 'received'
                  ? 'Ask your friends to recommend some great books!'
                  : 'Share your favorite books with friends to get started.'
                }
              </p>
            </div>
          ) : (
            <div className="recommendations-list">
              {recommendations.map(recommendation => (
                <RecommendationCard key={recommendation.id} recommendation={recommendation} />
              ))}
            </div>
          )}
        </div>

        {showRecommendModal && <RecommendModal />}
      </div>
    </div>
  );
}

export default BookRecommendations;